import { useEffect, useMemo, useState } from 'react';
import { get, kebabCase } from 'lodash';
import { matchSorter } from 'match-sorter';
import { useIntl } from 'react-intl';
import { HeaderTitle } from '@rossum/components/header/Header';
import { PageLayout } from '@rossum/components/page-layout/PageLayout';
import SearchInput from '@rossum/components/search-input/SearchInput';
import { IconApps } from '@rossum/ui/icons/tabler';
import {
  CircularProgress,
  Grid,
  Stack,
  SvgIcon,
  Typography,
} from '@rossum/ui/material';
import InfoBox from '../../../../components/UI/InfoBox';
import Loader from '../../../../containers/Loader';
import { pushToDataLayer } from '../../../../lib/pushToDataLayer';
import useDebounce from '../../../../utils/hooks/useDebounce';
import { ExtensionsHeader } from '../../components/ExtensionsHeader';
import {
  ExtendedHookTemplate,
  ExtensionDrawer,
} from './components/ExtensionDrawer';
import { useHookTemplatesQuery } from './hooks/useHookTemplatesQuery';
import { RossumStoreTile } from './RossumStoreTile';
import { StoreHints } from './StoreHints';

export const searchInExtensions = <T extends { name: string; tags: string }>(
  searchValue: string,
  results: T[] | undefined
) => {
  if (!results) return [];

  const normalizedSearchValue = searchValue.trim();

  if (!normalizedSearchValue.length) {
    return results;
  }

  return matchSorter(results, normalizedSearchValue, {
    keys: [
      { threshold: matchSorter.rankings.CONTAINS, key: 'name' },
      { threshold: matchSorter.rankings.CONTAINS, key: 'tags' },
    ],
  });
};

export const ExtensionsStorePage = () => {
  const intl = useIntl();
  const [selectedExtension, setSelectedExtension] =
    useState<ExtendedHookTemplate | null>(null);
  const [searchValue, setSearchValue] = useState<string>('');
  const debouncedSearchValue = useDebounce(searchValue, 500);

  const {
    data: results,
    status: extensionTemplatesStatus,
    isRefetching,
  } = useHookTemplatesQuery();

  const extendedResults = useMemo<ExtendedHookTemplate[] | undefined>(
    () =>
      results?.map(r => ({
        ...r,
        tags: r.storeDescription.match(/<p class="tags">(.+)<\/p>/)?.[1] ?? '',
      })),
    [results]
  );

  const extensionTemplateList = useMemo(() => {
    return searchInExtensions(searchValue, extendedResults);
  }, [searchValue, extendedResults]);

  useEffect(() => {
    if (debouncedSearchValue.length >= 3) {
      pushToDataLayer({
        event: 'rossumStoreSearch',
        searchValue: debouncedSearchValue,
      });
    }
  }, [debouncedSearchValue]);

  return (
    <PageLayout
      fullWidth
      renderHeader={params => (
        <ExtensionsHeader
          {...params}
          value="rossumStore"
          title={hasScrolled => (
            <Stack direction="row" alignItems="center" gap={1}>
              <HeaderTitle
                hasScrolled={hasScrolled}
                title={intl.formatMessage({
                  id: `containers.settings.sidebar.rossumStore`,
                })}
              />
              {isRefetching && <CircularProgress size={20} color="inherit" />}
            </Stack>
          )}
        />
      )}
    >
      <Stack spacing={3} p={4} width="100%" maxWidth={1400} mx="auto" flex={1}>
        <InfoBox
          icon={() => (
            <SvgIcon fontSize="large" color="inherit">
              <IconApps />
            </SvgIcon>
          )}
        >
          <Stack direction="row" spacing={0.5}>
            <Typography variant="subtitle2">
              {intl.formatMessage({
                id: 'containers.settings.extensions.store.instorePrebuilt',
              })}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {intl.formatMessage({
                id: 'containers.settings.extensions.store.instoreImprove',
              })}
            </Typography>
          </Stack>
        </InfoBox>
        <SearchInput
          fullWidth
          value={searchValue}
          onChange={value => setSearchValue(value)}
          sx={{ gridColumn: '1/-1' }}
          placeholder={intl.formatMessage({
            id: 'containers.settings.extensions.store.search.placeholder',
          })}
        />
        {extensionTemplatesStatus === 'success' ? (
          <Grid container spacing={2.5}>
            {extensionTemplateList.map(extension => {
              const { name, extensionImageUrl, description, type, config } =
                extension;
              return (
                <Grid key={name} size={{ xs: 12, sm: 6, md: 4 }}>
                  <RossumStoreTile
                    key={name}
                    name={name}
                    description={description}
                    imageUrl={extensionImageUrl}
                    handleClick={() => setSelectedExtension(extension)}
                    type={type}
                    runtime={get(config, ['runtime'])}
                    dataCy={`extension-store-${kebabCase(name)}`}
                  />
                </Grid>
              );
            })}
          </Grid>
        ) : (
          <Stack sx={{ height: '300px' }}>
            <Loader size={90} />
          </Stack>
        )}
        <StoreHints />
      </Stack>
      <ExtensionDrawer
        extension={selectedExtension}
        onClose={() => setSelectedExtension(null)}
      />
    </PageLayout>
  );
};
